import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, ExternalLink, FileText, Loader2 } from 'lucide-react';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import ScrollToTop from './ScrollToTop';
import WhatsAppButton from './WhatsAppButton';
import SEOHead from './SEOHead';
import axios from 'axios';
import { API, BACKEND_URL } from '../../config';

const ArrivalSchedulePage = () => {
  const [currency, setCurrency] = useState('USD');
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      const response = await axios.get(`${API}/public/arrival-schedules`);
      setSchedules(response.data);
    } catch (error) {
      console.error('Error fetching arrival schedules:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFullUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const handleOpenLink = (url) => {
    const fullUrl = getFullUrl(url);
    if (fullUrl) {
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Arrival Schedule - Global Trade Car"
        description="View the latest arrival schedules for imported vehicles. Download shipping schedules and tracking information."
      />
      <PublicHeader currency={currency} onCurrencyChange={setCurrency} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center">
              <Calendar className="h-10 w-10 text-white" />
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Arrival Schedule
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            View the latest arrival schedules for vehicles. Click on a schedule to view the full details or download the file.
          </p>
        </div>

        {/* Schedules */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-red-600" />
          </div>
        ) : schedules.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Arrival Schedules Available</h3>
              <p className="text-gray-600">
                Check back later for updated arrival schedules.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {schedules.map((schedule) => (
              <Card 
                key={schedule.id} 
                className="hover:shadow-xl transition-all cursor-pointer group"
                onClick={() => handleOpenLink(schedule.link_url)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center group-hover:bg-red-600 transition-colors">
                        <FileText className="h-6 w-6 text-red-600 group-hover:text-white transition-colors" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-red-600 transition-colors">
                        {schedule.title}
                      </h3>
                      {schedule.description && (
                        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                          {schedule.description}
                        </p>
                      )}
                      {schedule.link_url && (
                        <div className="flex items-center text-sm text-red-600 font-medium">
                          <ExternalLink className="h-4 w-4 mr-1" />
                          View Schedule
                        </div>
                      )}
                    </div>
                  </div>
                  {schedule.created_at && (
                    <p className="text-xs text-gray-400 mt-4">
                      Posted: {new Date(schedule.created_at).toLocaleDateString()}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Info Section */}
        <div className="mt-16">
          <Card className="bg-gray-50">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Need Help?</h2>
              <p className="text-gray-600 mb-4">
                If you have questions about your vehicle's arrival or need assistance with the schedule, 
                please don't hesitate to contact us.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="/contact" className="inline-block">
                  <button className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium">
                    Contact Us
                  </button>
                </a>
                <a href="/vehicles" className="inline-block">
                  <button className="px-6 py-3 border-2 border-red-600 text-red-600 rounded-lg hover:bg-red-50 transition-colors font-medium">
                    Browse Vehicles
                  </button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <PublicFooter />
      <ScrollToTop />
      <WhatsAppButton />
    </div>
  );
};

export default ArrivalSchedulePage;